/* Copyright (c) 2020-present CloudIO Inc */

import { camelCase } from 'lodash-es';
import { getManager } from '../../registry/Manager';
import { Platform } from '../../platform/Platform';
import {
  AliasType,
  ComponentManager,
  ComponentManagerFn,
  PropertyDefinition,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { DataStoreItem, defaultValue } from './DataStore';
import { analyzeDataStore } from './DataStoreAnalyze';
import { emptyFunction } from '@cloudio-saas/statex';

export function getNextAlias(alias: string, map?: Map<string, string>) {
  if (!map) return alias;
  let _alias = alias;
  let counter = 1;
  while (map.has(_alias)) {
    _alias = `${alias}${counter++}`;
  }
  return _alias;
}

export const displayName: PropertyDefinition = {
  label: 'Display Name',
  type: 'string',
  helpText:
    'e.g. `Employee` or `Sales Order` etc. Used to refer this store on messages displayed to the user.',
};

export const hideDirtyIndicator: PropertyDefinition = {
  label: 'Hide Dirty Indicator',
  type: 'boolean',
  helpText:
    'Check this to hide the dirty indicator displayed on the top left cornor of the field or grid cell when the data is updated by the user.',
};

export const controlledBy: PropertyDefinition = {
  label: 'Controlled By',
  type: 'ItemID',
  initialFilter: 'SmartSearch',
  validateItemId: (value, { pageId, appUid, itemId, platform }) => {
    const item = platform.getItem(appUid, pageId, value);
    if (item?.type !== 'SmartSearch') {
      return `Item id '${value}' doesn't belong to a Smart Search Component`;
    }
    return '';
  },
};

export const dsAppUidProp = (platform: Platform): PropertyDefinition => ({
  label: 'App',
  type: 'AppUidChooser',
  helpText: (item: DataStoreItem) => {
    if (item.props.dsAppUid === platform.appUid) {
      return "Warning! When you migrate this page to another app, it will connect to this app's data source. Leave this field blank otherwise.";
    }
    return "Leave blank to use the current app. The data will be fetched using the selected app's connection.";
  },
});

const DataStoreManager: ComponentManagerFn<'DataStore'> = (
  platform: Platform,
): ComponentManager<'DataStore'> => {
  let timer: NodeJS.Timeout;
  let callback = emptyFunction;
  return {
    ...baseComponentManager<'DataStore'>(platform, 'DataStore'),
    defaultValue,
    isChildAllowed: (page, item, childType) =>
      (
        [
          'TriggerBeforeQuery',
          'TriggerAfterQuery',
          'TriggerBeforeCreate',
          'TriggerBeforeSave',
          'TriggerAfterSave',
          'TriggerBeforeInsertStoreRecord',
          'TriggerAfterInsertStoreRecord',
          'TriggerBeforeDeleteRecord',
          'TriggerAfterDeleteRecord',
          'TriggerOnRecordActive',
        ] as ItemType[]
      ).includes(childType),
    onPropertyChange: (property, value, itemData, { appUid, pageId }) => {
      if (property === 'dsAppUid') {
        callback();
        return {
          [property]: value,
        };
      }
      if (property === 'dataSource' && typeof value === 'string') {
        const map = platform.getDataSources(appUid, pageId);
        const val = getNextAlias(`${camelCase(value)}Alias`, map);
        if (timer) {
          clearTimeout(timer);
        }
        timer = setTimeout(() => {
          platform.resetAliases(appUid, pageId);
        }, 1300);
        return {
          [property]: value,
          alias: val,
        };
      }
      return {
        [property]: value,
      };
    },
    isPropertyVisible: (property, item) => {
      switch (property) {
        case 'dsAppUid':
        case 'dataSource':
          return true;
        case 'alias':
          return !!item.dataSource;
        default:
          return !!item.alias;
      }
    },
    props: {
      dsAppUid: dsAppUidProp(platform),
      dataSource: {
        label: 'Data Source',
        type: 'DataSourceChooser',
        required: true,
        refreshCallback(cb) {
          callback = cb;
        },
      },
      alias: {
        label: 'Data Source Alias',
        type: 'DataSourceAlias',
        required: true,
      },
      displayName,
      insertable: {
        label: 'Insertable',
        type: 'booleanExpression',
      },
      updatable: {
        label: 'Updatable',
        type: 'booleanExpression',
      },
      deletable: {
        label: 'Deletable',
        type: 'boolean',
      },
      queryable: {
        label: 'Queryable',
        type: 'boolean',
      },
      autoQuery: {
        label: 'Auto Query',
        type: 'boolean',
      },
      maxRows: {
        label: 'Max Rows',
        type: 'number',
        min: 20,
        max: 2000,
        step: 20,
      },
      filterLocally: {
        label: 'Filter Locally',
        type: 'boolean',
      },
      singleton: {
        label: 'Singleton',
        type: 'boolean',
        helpText:
          'Singleton dataStores defined across different pages, with the same alias, will share the same state.',
      },
      cacheAll: {
        label: 'Cache All',
        type: 'boolean',
        helpText:
          'Check this to cache query result. With cache enabled, duplicate queries are not sent to the server.',
      },
      usePagination: {
        label: 'Use Pagination',
        type: 'boolean',
        helpText: 'Check this to use pagination component on this store.',
      },
      ignorePKDuplicate: {
        label: 'Ignore Primary Keys Duplicates',
        type: 'boolean',
        helpText:
          'Check this to ignore the duplicate primary key values on this store.',
      },
      maskPageOnQuery: {
        label: 'Mask Page On Query',
        type: 'boolean',
        helpText:
          'Check this to mask the page until the query request on store completed.',
      },
      maskPageOnPost: {
        label: 'Mask Page On Post',
        type: 'boolean',
        helpText:
          'Check this to mask the page until the post(save, server validate) request on store completed.',
      },
      doNotMonitorQueryRequest: {
        label: 'Do not Monitor Query Request',
        type: 'boolean',
        helpText: 'Check to disable monitoring query request.',
      },
      feedback: {
        label: 'Feedback Message',
        type: 'string',
        helpText:
          "The message to be shown after successful save. '#{count}' in the message will be replaced with the number of records posted. Specify NONE(without quotes) to skip it.",
      },
      controlledBy,
      hideDirtyIndicator,
    },
    category: 'Data',
    name: 'Data Store',
    canHaveChildren: () => true,
    getDataSourceAliasProperty: (type: AliasType) =>
      type === 'STORE' || type === 'QUERY_FILTER'
        ? { aliasProp: 'alias' }
        : undefined,
    getDisplayText: (item, expanded, { appUid, pageId }) => {
      if (!item.props.alias) return '';
      const page = platform.getPage(appUid, pageId);
      const itemIds = Object.keys(page);
      const usage = itemIds.filter((id) => {
        const itm = page[id];
        const mgr = getManager(itm.type);
        return mgr.isDataSourceAliasUsed(itm, item.props.alias);
      });
      return usage.length
        ? `${item.props.alias} (${usage.length} usage${
            usage.length > 1 ? 's' : ''
          })`
        : item.props.alias;
    },
    onCopy: (props, samePage) => ({
      ...props,
      alias: samePage ? '' : props.alias,
    }),
    generateShortId: (item) => item.alias,
    analyze: analyzeDataStore,
    icon: { iconName: 'database' },
    replaceableWith: [
      'ReadOnlyDataStore',
      'TransientDataStore',
      'LocalDataStore',
    ],
    description: `**Data Source** is a foundational component resides on the server side of the platform.
All the CRUD operations on the underlying database table or cloud service are performed via. the Data Source.

**Data Store** is a client-side component that houses all the data presented in the UI from a server-side dataSource. All the data fetching & manipulation happens through Stores.

The Data Store component creates an instance of the Store for the given dataSource.

As a best practice, always define all your editable stores using this component. These components are only allowed under the Page component to provide a better structure for your application. To enforce this best practice, you can only define store related triggers under the Data Store component.
`,
  };
};

export default DataStoreManager;
